import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import {
  GridApi,
  GridOptions,
  ColDef,
  GridReadyEvent,
  RowClickedEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IRowNode,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import './DealsTable.pcss';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import PageSize from 'lib/PageSize';
import { TableRowHeight } from 'lib/Table/Table.types';

import { DealFilters, FrontEndType } from 'components/common/types/Deal.types';
import { DealItemData, DealLineItemData } from 'components/common/types/DealItem';
import { Store } from 'components/common/types/Store.types';
import LoadingOverlayComponent from 'lib/Table/LoadingOverlayComponent';

import { PermissionsEnum } from 'consts/permissions';

import { cancelFunctions } from 'utils/cancelFunctions';
import { usePageFilters } from 'customHooks/usePageFilters';

import Auth from 'modules/Auth';
import Session from 'modules/Session';
import { getDealsWithBids, getDealLines } from 'modules/api/deals';
import { isCanceledError } from 'modules/api/request';

import { getValidPageSize } from 'utils/pageSize';

import { getLocaleCode } from 'store/publisher/selectors';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import { ActivatedNonMediaService, NonMediaServiceStatus } from 'components/common/types/NonMediaServices.types';
import { getSelectedNonMediaServices } from 'modules/api/nonMediaServices';
import { DealsTableProps, PerformanceFilters } from './DealsTable.types';
import { ColumnIds, sortColumnsBy } from './utils/utils';
import LinesExpandRenderer from './LinesExpandRenderer/LinesExpandRenderer';
import dealsTableColumnDefs from './DealsTableColumnDefs';
import ColumnHeader from './ColumnHeader/ColumnHeader';

const FRONTEND_TYPE_URLS: { [key in FrontEndType]: string } = {
  [FrontEndType.STANDARD]: '/deal',
  [FrontEndType.PLANNER]: '/planner/programmatic',
};

const pageKey = 'deal';

const DealsTable = ({ hasOutOfChargeEnabled, hasQuickDeliveryStatsEnabled }: DealsTableProps): React.ReactElement => {
  const localeCode = useSelector(getLocaleCode);
  const configEnabledLineLevelTrading = useSelector((state: Store) => state.dealConfig.enableLineLevelTrading);
  const [pageSize, setPageSize] = useState(getValidPageSize(Session.getPageSizeByKey(pageKey)));

  const navigate = useNavigate();
  const hasOnlyAllowPlanner = useHasFeatureAccess(FeatureFlags.ONLY_ALLOW_PLANNER);
  const tableApiRef: MutableRefObject<GridApi | undefined> = useRef();
  const isInitialMountRef = useRef(true);

  const { filters, changeFilters } = usePageFilters<
    DealFilters | Pick<PerformanceFilters, 'deal' | 'startDate' | 'endDate'>
  >('insights/');

  const filtersRef = useRef(filters);
  const pageSizeRef = useRef(pageSize);
  filtersRef.current = filters;
  pageSizeRef.current = pageSize;

  const hasRevenueViewPermission = Auth.hasPermission(PermissionsEnum.DEAL_MANAGEMENT_REVENUE_VIEW);
  const hasServicesViewEnabled = useHasFeatureAccess(FeatureFlags.NON_MEDIA_SERVICES_ACCESS);

  const onPieChartClick = (data: DealItemData): void => {
    navigate('/insights');
    changeFilters({
      deal: { code: data.dealId ?? '', internalId: data.internalId?.toString() ?? '', name: data.dealName ?? '' },
      startDate: undefined,
      endDate: undefined,
    });
  };

  const fetchServices = async (
    lines: DealLineItemData[],
    dealId: string,
  ): Promise<{ selectedServices: ActivatedNonMediaService[] | null }> => {
    if (!hasServicesViewEnabled || !lines.some((line) => line.hasServiceActivations)) return { selectedServices: null };
    try {
      const selectedServices = await getSelectedNonMediaServices(cancelFunctions, {
        dealIds: [],
        campaignIds: [dealId],
        activationIds: [],
      });

      return { selectedServices };
    } catch {
      return { selectedServices: null };
    }
  };

  const fetchLines = async (parentNode: IRowNode): Promise<{ rowData: DealLineItemData[]; rowCount: number }> => {
    const { dealId } = parentNode.data;

    const lines = await getDealLines({
      cancelFunctions,
      dealId,
    });

    const { selectedServices } = await fetchServices(lines, dealId);

    const childRows = lines.map((line: Record<string, DealItemData>, index: number) => {
      const selectedLineServices = selectedServices?.filter((service) => service.dealId === line.lineId);

      const approvedServicesCount = selectedLineServices?.filter(
        (service) => service.status === NonMediaServiceStatus.APPROVED,
      ).length;
      const cancelledServicesCount = selectedLineServices?.filter(
        (service) =>
          service.status === NonMediaServiceStatus.CANCELLED || service.status === NonMediaServiceStatus.REJECTED,
      ).length;

      return {
        ...line,
        isChildRow: true,
        hierarchy: [dealId, line.lineId || line.internalId],
        lineIndex: index,
        approvedServicesCount,
        cancelledServicesCount,
      };
    });

    return { rowData: childRows, rowCount: childRows.length };
  };

  const fetchDeals = async (
    request: IServerSideGetRowsRequest,
    pageSizeParam: number,
  ): Promise<{ rowData: DealItemData[]; rowCount: number }> => {
    const [sortingColumn] = request.sortModel;
    tableApiRef.current?.showLoadingOverlay();

    const { content, totalElements } = await getDealsWithBids({
      cancelFunctions,
      filters: filtersRef.current,
      page: Math.floor((request.startRow ?? 0) / pageSizeParam),
      size: pageSizeParam,
      sort: sortingColumn
        ? {
            sortBy: sortColumnsBy(sortingColumn.colId as keyof ColumnIds),
            sortDir: sortingColumn.sort?.toUpperCase(),
          }
        : {},
      cancelName: 'GET_DEALS',
    });

    if (totalElements === 0) {
      tableApiRef.current?.showNoRowsOverlay();
    } else {
      tableApiRef.current?.hideOverlay();
    }

    const dealsWithHierarchy = content.map((deal: DealItemData) => ({
      ...deal,
      hierarchy: [deal.dealId],
      isChildRow: false,
    }));

    return { rowData: dealsWithHierarchy, rowCount: totalElements };
  };

  const createSkeletonRows = (dealId: string, count: number): DealItemData[] => {
    return Array.from({ length: count }, (_, index) => ({
      dealId,
      isChildRow: true,
      isSkeletonRow: true,
      lineIndex: index,
      hierarchy: [dealId, `skeleton-${dealId}-${index}`],
    }));
  };

  const getDatasource = useCallback(
    (pageSizeParam: number): IServerSideDatasource => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        const { parentNode, request, success, fail } = params;

        try {
          const isChildRequest = parentNode?.data !== undefined;

          if (isChildRequest) {
            const dealId = parentNode.data?.dealId;
            const expectedLineCount = parentNode.data?.totalLineCount ?? 3;

            const skeletonRows = createSkeletonRows(dealId, Math.min(expectedLineCount, 5));
            success({ rowData: skeletonRows, rowCount: skeletonRows.length });

            const { rowData } = await fetchLines(parentNode);

            const updatedRowData = rowData.map((row) => ({
              ...row,
              enableLineLevelTrading: parentNode.data?.enableLineLevelTrading ?? false,
            }));

            tableApiRef.current?.applyServerSideTransaction({
              route: [dealId],
              remove: skeletonRows,
              add: updatedRowData,
            });
          } else {
            tableApiRef.current?.showLoadingOverlay();
            const { rowData, rowCount } = await fetchDeals(request, pageSizeParam);

            success({ rowData, rowCount });
          }
        } catch (error) {
          if (isCanceledError(error)) return;

          tableApiRef.current?.hideOverlay();
          fail();
        }
      },
    }),
    [],
  );

  const onGridReady = (params: GridReadyEvent): void => {
    tableApiRef.current = params.api;
    params.api.setServerSideDatasource(getDatasource(pageSize));
  };

  const onRowClicked = (params: RowClickedEvent<DealItemData>): void => {
    if (!params.data) return;

    const {
      event,
      data: { hierarchy = [], frontEndType = FrontEndType.STANDARD },
    } = params;

    if (event?.defaultPrevented) return;

    const [dealId, lineId] = hierarchy;

    event?.preventDefault?.();

    const preferredDealType = localStorage.getItem('preference');
    const openInPlanner =
      hasOnlyAllowPlanner || preferredDealType === 'planner' || frontEndType === FrontEndType.PLANNER;

    const editUrl = openInPlanner
      ? FRONTEND_TYPE_URLS[FrontEndType.PLANNER]
      : FRONTEND_TYPE_URLS[FrontEndType.STANDARD];

    navigate(`${editUrl}/${dealId}`, { state: { lineId } });
  };

  const onPageSizeChange = (size: number): void => {
    setPageSize(size);
    Session.setPageSizeByKey(pageKey, size);
  };

  useEffect(() => {
    return () => {
      if (cancelFunctions.GET_DEALS) cancelFunctions.GET_DEALS();
      if (cancelFunctions.DEAL_LIST_BIDS_BULK) cancelFunctions.DEAL_LIST_BIDS_BULK();
      if (cancelFunctions.FETCH_DEAL_LINES) cancelFunctions.FETCH_DEAL_LINES();
    };
  }, []);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (!tableApiRef.current) return;
    tableApiRef.current.setServerSideDatasource(getDatasource(pageSizeRef.current));
  }, [filters]);

  const isServerSideGroup = useCallback((dataItem: DealItemData): boolean => {
    if (dataItem.isChildRow) return false;
    return (dataItem.totalLineCount ?? 0) > 0;
  }, []);

  const getServerSideGroupKey = useCallback((dataItem: DealItemData): string => {
    return dataItem.dealId ?? '';
  }, []);

  const autoGroupColumnDef: ColDef = {
    field: 'dealName',
    headerComponent: ColumnHeader,
    headerName: 'Name',
    minWidth: 350,
    resizable: true,
    cellRenderer: LinesExpandRenderer,
    cellRendererParams: {
      suppressCount: true,
      suppressDoubleClickExpand: true,
      hasOutOfChargeEnabled,
    },
  };

  const getRowId = useCallback((params: { data: DealItemData | DealLineItemData }): string => {
    if (params.data?.isSkeletonRow) {
      return `skeleton-${params.data.dealId}-${(params.data as DealLineItemData).lineIndex}`;
    }
    if (params.data?.isChildRow) {
      return `${params.data.dealId}-${(params.data as DealLineItemData).lineId || params.data.internalId}`;
    }
    return params.data?.dealId || params.data?.internalId || '';
  }, []);

  const gridOptions: GridOptions = {
    animateRows: true,
    cacheBlockSize: pageSize,
    domLayout: 'autoHeight',
    enableCellTextSelection: true,
    pagination: true,
    paginationPageSize: pageSize,
    rowHeight: TableRowHeight.NORMAL,
    rowModelType: 'serverSide',
    rowSelection: 'multiple',
    suppressAggFuncInHeader: true,
    suppressRowVirtualisation: true,
    loadingCellRenderer: () => null,
  };

  return (
    <>
      <div className="mb-3">
        <PageSize
          dataTestId="page-size-selection"
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
          pageInfoText="campaigns per page"
        />
      </div>
      <div className="campaigns-table ag-theme-alpine" data-test-id="deals-table" style={{ width: '100%' }}>
        <AgGridReact
          key={pageSize}
          columnDefs={dealsTableColumnDefs({
            localeCode,
            hasRevenueViewPermission,
            hasOutOfChargeEnabled,
            hasQuickDeliveryStatsEnabled,
            onPieChartClick,
            configEnabledLineLevelTrading,
            hasServicesViewEnabled,
          })}
          loadingOverlayComponent={LoadingOverlayComponent}
          gridOptions={gridOptions}
          autoGroupColumnDef={autoGroupColumnDef}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          treeData
          isServerSideGroup={isServerSideGroup}
          getServerSideGroupKey={getServerSideGroupKey}
          getRowId={getRowId}
        />
      </div>
    </>
  );
};

export default DealsTable;
